import React, { useState } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Stack,
  TextField,
  CircularProgress,
} from "@mui/material";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { useTranslation } from "react-i18next";
import dayjs, { Dayjs } from "dayjs";

interface VersionEffectiveDateDialogProps {
  open: boolean;
  onClose: () => void;
  onConfirm: (date: Dayjs) => void;
  isLoading: boolean;
}

const VersionEffectiveDateDialog: React.FC<VersionEffectiveDateDialogProps> = ({
  open,
  onClose,
  onConfirm,
  isLoading,
}) => {
  const { t, i18n } = useTranslation();

  // Default to tomorrow
  const [effectiveDate, setEffectiveDate] = useState<Dayjs | null>(
    dayjs().add(1, "day")
  );

  const handleConfirm = () => {
    if (effectiveDate) {
      onConfirm(effectiveDate);
    }
  };

  const getFormattedDate = (date: Dayjs) => {
    if (i18n.language.startsWith("es")) {
      return new Intl.DateTimeFormat("es-ES", { dateStyle: "long" }).format(
        date.toDate()
      );
    }
    const d = date.date();
    const getOrdinal = (n: number) => {
      const s = ["th", "st", "nd", "rd"];
      const v = n % 100;
      return n + (s[(v - 20) % 10] || s[v] || s[0]);
    };
    const month = new Intl.DateTimeFormat("en-US", { month: "long" }).format(
      date.toDate()
    );
    return `${getOrdinal(d)} of ${month} of ${date.year()}`;
  };

  const formattedDate = effectiveDate ? getFormattedDate(effectiveDate) : null;
  const tomorrow = dayjs().add(1, "day").startOf("day");
  // Optional: prevent selecting past dates (or today if strictly T+1 constraint)
  const isDateValid = effectiveDate && !effectiveDate.isBefore(tomorrow, "day");

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>
        {t("briefingsView.finalizeVersionTitle", "Finalize Amendment")}
      </DialogTitle>
      <DialogContent>
        <Stack spacing={3} sx={{ mt: 1 }}>
          <Typography variant="body2" color="text.secondary">
            {t(
              "briefingsView.finalizeVersionDesc",
              "You are about to finalize a modified version of this briefing. Please select the Effective Date from which the new investment and margins will start applying. The original briefing will end one day prior to this date."
            )}
          </Typography>

          <Stack spacing={1}>
            <LocalizationProvider dateAdapter={AdapterDayjs}>
              <DatePicker
                label={t("briefingsView.effectiveDate", "Effective Date")}
                value={effectiveDate}
                onChange={(newValue) => setEffectiveDate(newValue)}
                minDate={tomorrow}
                renderInput={(params) => (
                  <TextField {...params} fullWidth required />
                )}
                disableMaskedInput
              />
            </LocalizationProvider>
            {formattedDate && (
              <Typography
                variant="body2"
                color={isDateValid ? "primary" : "error"}
                sx={{ px: 1, fontWeight: 500 }}
              >
                {t(
                  "briefingsView.selectedEffectiveDateInfo",
                  "Margin shifts on: {{date}}",
                  { date: formattedDate }
                )}
              </Typography>
            )}
          </Stack>
        </Stack>
      </DialogContent>
      <DialogActions sx={{ p: 2 }}>
        <Button onClick={onClose} color="inherit" disabled={isLoading}>
          {t("common.cancel", "Cancel")}
        </Button>
        <Button
          onClick={handleConfirm}
          variant="contained"
          color="success"
          disabled={!isDateValid || isLoading}
          startIcon={isLoading ? <CircularProgress size={20} /> : undefined}
        >
          {t("common.confirm", "Confirm")}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default VersionEffectiveDateDialog;
